import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import * as XLSX from 'xlsx';

/**
 * Eenheid waarin de belastingkolom van het bronbestand staat.
 *
 * WAAROM DIT EXPLICIET IS EN NOOIT GERADEN WORDT (T4.2, 8 september 2026)
 * -----------------------------------------------------------------------
 * Het methodiekdocument beloofde op `flexeMethodologyDocument.ts:49-52` letterlijk:
 * "Indien data in kWh wordt aangeleverd, converteert de tool naar kW: kW = kWh x 4
 * (kwartierwaarde x 4 = momentaan vermogen)". Die conversie bestond nergens in de keten.
 * De parser nam de kolomwaarde rauw over -- de oude regel 103 was `const kw = parseFloat(loadStr)`
 * en regel 187 hetzelfde voor Excel -- en over de hele upload-/parse-/simulatieketen stond
 * geen enkele `* 4`, `/ 0.25`, `isKwh` of `unit`.
 *
 * Wat dat kost, gemeten op de enige echte dag die we hebben (simulatie
 * 7486a1c2-205e-43c5-98e1-b811d44d4cd8, slechtste dag 2025-11-18, baseline-kolom,
 * contractlimiet 135 kW, de 20 kwartieren in het venster 16:00-21:00):
 *
 *   gelezen als kW              -> energietekort    102,9825 kWh, piektekort  48,35 kW
 *   werkelijk kWh per kwartier  -> energietekort  2.428,62   kWh, piektekort 598,40 kW
 *
 * Dat is 23,6x te weinig energie en 12,4x te weinig vermogen. Het is dus geen nette factor 4:
 * de aftrek van de contractlimiet is niet lineair, dus de fout groeit harder dan de invoer.
 * En het oordeel klapt om: met de opgeslagen batterij (200 kW / 400 kWh) is 48,35 <= 200 en
 * 102,9825 <= 400 ("past, 15% marge"), terwijl 598,40 > 200 en 2.428,62 > 400 -- beide toetsen
 * falen. Een vier keer te lage piek levert een veel te kleine batterij, en dat gebeurde stil.
 *
 * De eenheid is NIET uit de waarden af te leiden: een gebouw met een piek van 100 kW en een
 * gebouw met kwartierwaarden van 100 kWh zien er in de kolom identiek uit. Daarom wordt de
 * eenheid vastgesteld en nooit geraden, in deze volgorde:
 *   1. expliciete keuze van de gebruiker (`options.unit`, bedoeld voor het uploadscherm);
 *   2. een eenheid die het bronbestand zelf in de kolomkop verklaart ("Piekbelasting (kW)");
 *   3. de vraag aan de gebruiker (`options.askUnit`, standaard twee bevestigingen).
 * Lukt geen van drie, dan gaat de upload NIET door. Er is geen stille aanname meer.
 */
export type FlexELoadUnit = 'kW' | 'kWh_per_kwartier';

/** Waar de vastgestelde eenheid vandaan komt. Gaat mee naar het resultaat zodat het zichtbaar is. */
export type FlexEUnitSource = 'expliciet' | 'kolomkop_bevestigd' | 'gevraagd';

export const FLEXE_UNIT_LABEL: Record<FlexELoadUnit, string> = {
  kW: 'kW (momentaan vermogen)',
  kWh_per_kwartier: 'kWh per kwartier (x4 omgerekend naar kW)'
};

export const FLEXE_UNIT_SOURCE_LABEL: Record<FlexEUnitSource, string> = {
  expliciet: 'gekozen bij het uploaden',
  kolomkop_bevestigd: 'uit de kolomkop, door de gebruiker bevestigd',
  gevraagd: 'gevraagd, want de kolomkop zei het niet'
};

/** kW = kolomwaarde x factor. Een kwartierwaarde in kWh x 4 is het momentane vermogen. */
export const flexeUnitToKwFactor = (unit: FlexELoadUnit): number =>
  unit === 'kWh_per_kwartier' ? 4 : 1;

/**
 * Leest de eenheid uit de kolomkop -- dus uit wat de leverancier van het bestand zelf opschrijft,
 * niet uit de getallen. Geeft `null` zodra de kop het niet, of dubbel, verklaart; dan wordt er
 * gevraagd. "kwartier" wordt eerst weggeknipt omdat dat woord "kw" bevat en anders vals kW zou
 * verklaren (de fuzzy kolomherkenning hieronder matcht juist op `kwartier`).
 */
export function flexeDeclaredUnitFromHeader(header: string): FlexELoadUnit | null {
  const probe = (header || '')
    .toLowerCase()
    .replace(/kwart\w*/g, '')            // kwartier, kwartierwaarde, kwartaal
    .replace(/[\s._-]/g, '');

  const saysKwh = /kwh/.test(probe) || /kilowattuur/.test(probe);
  const saysKw = /kw(?!h)/.test(probe) || /kilowatt(?!uur)/.test(probe);

  if (saysKwh && !saysKw) return 'kWh_per_kwartier';
  if (saysKw && !saysKwh) return 'kW';
  return null; // geen eenheid, of allebei -> niet vastgesteld
}

export interface FlexEUnitQuestion {
  filename: string;
  loadHeader: string;
  rowCount: number;
  /**
   * Wat de kolomkop zelf zegt, of `null` als hij er niets over zegt. Dit is een
   * VOORSTEL en geen besluit: het vult de vraag voor, maar er wordt altijd
   * gevraagd. Een kop die "kwh" heet is namelijk niet hetzelfde als iemand die
   * bevestigt dat de waarden kWh per kwartier zijn — en een stille x4 op grond
   * van een kop is precies dezelfde soort fout als de stille x1 die dit hele
   * ticket moest wegnemen, alleen met een andere aanleiding.
   */
  vermoeden: FlexELoadUnit | null;
}

/** Geeft de gekozen eenheid terug, of `null` om de upload af te breken. */
export type FlexEAskUnit = (
  question: FlexEUnitQuestion
) => FlexELoadUnit | null | Promise<FlexELoadUnit | null>;

/**
 * Standaardvraag als het uploadscherm er (nog) geen eigen keuze voor toont. Twee bevestigingen,
 * zodat alle drie de uitkomsten -- kW, kWh, afbreken -- een eigen klik hebben en geen enkele
 * uitkomst per ongeluk uit "Annuleren" volgt. Zelfde patroon als `useZymplerImport.ts:40`.
 */
const confirmUnitDialog: FlexEAskUnit = ({ filename, loadHeader, vermoeden }) => {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return null;

  const aanhef = vermoeden
    ? `De kolomkop "${loadHeader}" in ${filename} wijst op ${FLEXE_UNIT_LABEL[vermoeden]}. Klopt dat?\n\n`
    : `Eenheid van kolom "${loadHeader}" in ${filename} staat niet in het bestand.\n\n`;

  const isKw = window.confirm(
    aanhef +
      `Staan de waarden in kW (momentaan vermogen)?\n\n` +
      `OK = ja, kW\n` +
      `Annuleren = nee, een andere eenheid`
  );
  if (isKw) return 'kW';

  const isKwh = window.confirm(
    `Staan de waarden dan in kWh per kwartier?\n\n` +
      `OK = ja, reken om naar kW (waarde x 4)\n` +
      `Annuleren = upload afbreken\n\n` +
      `Let op: de verkeerde eenheid maakte op de gemeten dag de piek 12,4x te laag ` +
      `(48,35 kW in plaats van 598,40 kW) en daarmee de batterij veel te klein.`
  );
  return isKwh ? 'kWh_per_kwartier' : null;
};

export interface FlexEUploadOptions {
  /** Expliciete eenheidskeuze uit het uploadscherm. Wint altijd van kolomkop en vraag. */
  unit?: FlexELoadUnit;
  /** Eigen manier om de eenheid te vragen (bijv. een dialoog i.p.v. de standaard bevestigingen). */
  askUnit?: FlexEAskUnit;
}

interface UploadResult {
  upload_id: string;
  filename: string;
  rows: number;
  date_start: string;
  date_end: string;
  preview: Array<{ timestamp: string; kw: number }>;
  /** Eenheid waarin het bronbestand is gelezen. Niet van de server: hier vastgesteld. */
  load_unit: FlexELoadUnit;
  unit_source: FlexEUnitSource;
}

interface QuarterData {
  timestamp: string;
  kw: number;
}

/** Rij zoals hij in het bestand staat: de rauwe kolomwaarde, eenheid nog onbekend. */
interface RawQuarterRow {
  timestamp: string;
  value: number;
}

export function useFlexEUpload() {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { toast } = useToast();

  // Fuzzy match column headers
  const fuzzyMatch = (header: string, patterns: string[]): boolean => {
    const normalized = header.toLowerCase().trim();
    return patterns.some(p => normalized.includes(p.toLowerCase()));
  };

  // Parse CSV client-side
  const parseCSV = async (file: File): Promise<{ rows: RawQuarterRow[]; timeHeader: string; loadHeader: string }> => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    
    if (lines.length < 2) {
      throw new Error('Bestand bevat geen data');
    }

    // Parse header
    const headers = lines[0].split(/[,;\t]/).map(h => h.trim().replace(/^["']|["']$/g, ''));
    
    // Find time and load columns
    const timePatterns = ['tijd', 'time', 'timestamp', 'date', 'datum', 'datetime', 'van'];
    const loadPatterns = ['piek', 'load', 'kw', 'power', 'vermogen', 'kwartier', 'piekbelasting'];
    
    const timeColIndex = headers.findIndex(h => fuzzyMatch(h, timePatterns));
    const loadColIndex = headers.findIndex(h => fuzzyMatch(h, loadPatterns));
    
    if (timeColIndex === -1 || loadColIndex === -1) {
      throw new Error('Vereiste kolommen (tijd + kW) niet gevonden. Zorg voor kolommen met "tijd" en "kW" in de header.');
    }

    const timeHeader = headers[timeColIndex];
    const loadHeader = headers[loadColIndex];
    
    console.info(`[CSV] Detected headers: time="${timeHeader}", load="${loadHeader}"`);

    // Parse data rows
    const rows: RawQuarterRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(/[,;\t]/).map(v => v.trim().replace(/^["']|["']$/g, ''));
      
      if (values.length <= Math.max(timeColIndex, loadColIndex)) continue;

      const timeStr = values[timeColIndex];
      const loadStr = values[loadColIndex].replace(',', '.');
      
      if (!timeStr || !loadStr) continue;

      // Parse timestamp - handle formats like "1-1-2024 00:00:01"
      let timestamp = timeStr;
      
      // Handle Dutch date format: "1-1-2024 00:00:01" or "01-01-2024 00:00:01"
      if (/^\d{1,2}-\d{1,2}-\d{4}/.test(timeStr)) {
        const parts = timeStr.split(' ');
        const dateParts = parts[0].split('-');
        const day = dateParts[0].padStart(2, '0');
        const month = dateParts[1].padStart(2, '0');
        const year = dateParts[2];
        const time = parts[1] || '00:00:00';
        timestamp = `${year}-${month}-${day} ${time}`;
      }
      // Handle time-only formats
      else if (/^\d{1,2}[:\-]\d{2}/.test(timeStr) && !/^\d{4}/.test(timeStr)) {
        timestamp = `2024-01-01 ${timeStr}`;
      }
      // Handle other formats
      else if (!/\d{4}-\d{2}-\d{2}/.test(timestamp)) {
        const parsed = new Date(timeStr);
        if (!isNaN(parsed.getTime())) {
          timestamp = parsed.toISOString().slice(0, 19).replace('T', ' ');
        } else {
          continue; // Skip invalid timestamps
        }
      }
      
      // Ensure timestamp always has seconds component
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(timestamp)) {
        timestamp += ':00';
      }

      // Rauwe kolomwaarde; de omrekening naar kW gebeurt pas als de eenheid vaststaat.
      const value = parseFloat(loadStr);
      if (isNaN(value)) continue;

      rows.push({ timestamp, value });
    }
    
    console.info(`[CSV] Parsed ${rows.length} rows. First 2 timestamps:`, rows.slice(0, 2).map(r => r.timestamp));

    return { rows, timeHeader, loadHeader };
  };

  // Parse Excel client-side
  const parseExcel = async (file: File): Promise<{ rows: RawQuarterRow[]; timeHeader: string; loadHeader: string }> => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false, dateNF: 'yyyy-mm-dd hh:mm:ss' });

    if (jsonData.length < 2) {
      throw new Error('Bestand bevat geen data');
    }

    // Parse header
    const headers = (jsonData[0] as any[]).map(h => String(h || '').trim());
    
    // Find columns
    const timePatterns = ['tijd', 'time', 'timestamp', 'date', 'datum', 'datetime', 'van'];
    const loadPatterns = ['piek', 'load', 'kw', 'power', 'vermogen', 'kwartier', 'piekbelasting'];
    
    const timeColIndex = headers.findIndex(h => fuzzyMatch(h, timePatterns));
    const loadColIndex = headers.findIndex(h => fuzzyMatch(h, loadPatterns));
    
    if (timeColIndex === -1 || loadColIndex === -1) {
      throw new Error('Vereiste kolommen (tijd + kW) niet gevonden. Zorg voor kolommen met "tijd" en "kW" in de header.');
    }

    const timeHeader = headers[timeColIndex];
    const loadHeader = headers[loadColIndex];
    
    console.info(`[EXCEL] Detected headers: time="${timeHeader}", load="${loadHeader}"`);

    // Parse data rows
    const rows: RawQuarterRow[] = [];
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i] as any[];
      if (!row || row.length <= Math.max(timeColIndex, loadColIndex)) continue;

      const timeStr = String(row[timeColIndex] || '').trim();
      const loadStr = String(row[loadColIndex] || '').trim().replace(',', '.');
      
      if (!timeStr || !loadStr) continue;

      // Parse timestamp - handle formats like "1-1-2024 00:00:01"
      let timestamp = timeStr;
      
      // Handle Dutch date format: "1-1-2024 00:00:01" or "01-01-2024 00:00:01"
      if (/^\d{1,2}-\d{1,2}-\d{4}/.test(timeStr)) {
        const parts = timeStr.split(' ');
        const dateParts = parts[0].split('-');
        const day = dateParts[0].padStart(2, '0');
        const month = dateParts[1].padStart(2, '0');
        const year = dateParts[2];
        const time = parts[1] || '00:00:00';
        timestamp = `${year}-${month}-${day} ${time}`;
      }
      // Handle time-only formats
      else if (/^\d{1,2}[:\-]\d{2}/.test(timeStr) && !/^\d{4}/.test(timeStr)) {
        timestamp = `2024-01-01 ${timeStr}`;
      }
      // Handle other formats
      else if (!/\d{4}-\d{2}-\d{2}/.test(timestamp)) {
        const parsed = new Date(timeStr);
        if (!isNaN(parsed.getTime())) {
          timestamp = parsed.toISOString().slice(0, 19).replace('T', ' ');
        } else {
          continue; // Skip invalid timestamps
        }
      }
      
      // Ensure timestamp always has seconds component
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(timestamp)) {
        timestamp += ':00';
      }

      // Rauwe kolomwaarde; de omrekening naar kW gebeurt pas als de eenheid vaststaat.
      const value = parseFloat(loadStr);
      if (isNaN(value)) continue;

      rows.push({ timestamp, value });
    }
    
    console.info(`[EXCEL] Parsed ${rows.length} rows. First 2 timestamps:`, rows.slice(0, 2).map(r => r.timestamp));

    return { rows, timeHeader, loadHeader };
  };

  /**
   * Stelt de eenheid vast. Kijkt nooit naar de waarden zelf -- alleen naar wat er expliciet
   * gekozen is, wat het bestand in zijn kolomkop verklaart, en anders naar het antwoord van
   * de gebruiker. `null` betekent: niet vastgesteld, upload gaat niet door.
   */
  const resolveLoadUnit = async (
    // Zonder `vermoeden`: dat vult deze functie zelf in uit de kolomkop, zodat
    // geen enkele aanroeper hem kan overslaan of verkeerd kan invullen.
    question: Omit<FlexEUnitQuestion, 'vermoeden'>,
    options?: FlexEUploadOptions
  ): Promise<{ unit: FlexELoadUnit; source: FlexEUnitSource } | null> => {
    if (options?.unit) {
      return { unit: options.unit, source: 'expliciet' };
    }

    // De kolomkop VULT DE VRAAG VOOR maar beslist niet. Gemeten 08-09-2026 in
    // flexe_uploads: van de dertien uploads draagt er precies een de kop 'kwh'
    // (Houten (Plesman) - CRM.xlsx, 35.136 rijen, 26-01-2026). Die zou met een
    // automatische aflezing stilzwijgend x4 gaan -- geen enkele simulatie hangt
    // er vandaag aan, dus er verschuift nu niets, maar het patroon deugt niet.
    const declared = flexeDeclaredUnitFromHeader(question.loadHeader);
    const answer = await (options?.askUnit ?? confirmUnitDialog)({ ...question, vermoeden: declared });
    if (!answer) return null;
    return { unit: answer, source: declared === answer ? 'kolomkop_bevestigd' : 'gevraagd' };
  };

  /**
   * `options` staat achteraan zodat de bestaande aanroep `uploadFile(file)` blijft werken;
   * de vierde parameter was `retries = 2` en werd nergens in de functie gebruikt.
   *
   * Voor het uploadscherm (`src/components/flexe/FlexEUploadCard.tsx`, buiten deze wijziging):
   * geef de keuze van de gebruiker door als `uploadFile(file, undefined, undefined, { unit })`
   * en toon `result.load_unit` / `result.unit_source` naast de regel met het aantal rijen.
   * Zolang dat scherm niets meestuurt, vraagt deze hook het zelf (twee bevestigingen) en staat
   * de bevestiging alleen in de melding na afloop -- dus zichtbaar, maar niet blijvend in beeld.
   */
  const uploadFile = async (
    file: File,
    siteId?: string,
    projectId?: string,
    options?: FlexEUploadOptions
  ): Promise<UploadResult | null> => {
    setUploading(true);
    setUploadProgress(0);

    try {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Bestand te groot. Maximale grootte is 10MB');
      }

      setUploadProgress(10);

      // Parse file client-side
      console.log('Parsing file client-side:', file.name);
      let rawRows: RawQuarterRow[];
      let timeHeader: string;
      let loadHeader: string;

      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.csv')) {
        const result = await parseCSV(file);
        rawRows = result.rows;
        timeHeader = result.timeHeader;
        loadHeader = result.loadHeader;
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        const result = await parseExcel(file);
        rawRows = result.rows;
        timeHeader = result.timeHeader;
        loadHeader = result.loadHeader;
      } else {
        throw new Error('Ongeldig bestandstype. Upload alleen .csv, .xlsx of .xls bestanden');
      }

      setUploadProgress(40);

      if (rawRows.length === 0) {
        throw new Error('Geen geldige data gevonden. Controleer of het bestand tijd- en kW-kolommen bevat');
      }

      if (rawRows.length > 400000) {
        throw new Error(`Te veel rijen: ${rawRows.length}. Maximum is 400,000 rijen`);
      }

      // Eenheid vaststellen VOORDAT er ook maar iets wordt weggeschreven.
      const decision = await resolveLoadUnit(
        { filename: file.name, loadHeader, rowCount: rawRows.length },
        options
      );

      if (!decision) {
        // Geen eenheid, dus geen upload. Liever niets dan een stille factor-4-fout.
        toast({
          title: 'Upload afgebroken',
          description: `De eenheid van kolom "${loadHeader}" is niet bevestigd. Zonder eenheid kan de piek niet worden bepaald.`
        });
        return null;
      }

      // Hier gebeurt de conversie die het methodiekdocument al beloofde:
      // kW = kwartierwaarde in kWh x 4. Bij een kW-bestand is de factor 1, dus dan zijn de
      // rijen bit voor bit gelijk aan wat deze parser voor de reparatie opleverde.
      const factor = flexeUnitToKwFactor(decision.unit);
      const rows: QuarterData[] = rawRows.map(r => ({ timestamp: r.timestamp, kw: r.value * factor }));

      console.log(
        `Parsed ${rows.length} rows as ${decision.unit} (${decision.source}), kW factor ${factor}, uploading to server...`
      );

      // Get session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Niet ingelogd. Log opnieuw in en probeer het opnieuw');
      }

      setUploadProgress(50);

      // Upload to new edge function.
      // `load_unit` gaat mee zodat de herkomst ooit in `flexe_uploads.metadata` kan landen;
      // de edge function `flexe-save-upload` leest vandaag alleen site_id, project_id, filename,
      // time_header, load_header en rows (index.ts:58) en negeert dit veld dus nog.
      // De verzonden `rows` staan hoe dan ook al in kW.
      const { data, error } = await supabase.functions.invoke('flexe-save-upload', {
        body: {
          site_id: siteId,
          project_id: projectId,
          filename: file.name,
          time_header: timeHeader,
          load_header: loadHeader,
          load_unit: decision.unit,
          rows
        }
      });

      setUploadProgress(100);

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Upload mislukt');
      }

      // De bevestiging staat in de melding zelf: wat er is gelezen, en waar die eenheid vandaan komt.
      toast({
        title: 'Upload geslaagd',
        description:
          `${data.rows} rijen verwerkt (${data.date_start} tot ${data.date_end}) - ` +
          `gelezen als ${FLEXE_UNIT_LABEL[decision.unit]}, ${FLEXE_UNIT_SOURCE_LABEL[decision.source]}`
      });

      return {
        ...(data as UploadResult),
        load_unit: decision.unit,
        unit_source: decision.source
      };

    } catch (error: any) {
      console.error('Upload error:', error);
      
      let errorTitle = 'Upload mislukt';
      let errorDescription = error.message || 'Er is een onbekende fout opgetreden';
      
      // Handle specific error codes from server
      if (error.message?.includes('UNAUTHORIZED')) {
        errorTitle = 'Niet geautoriseerd';
        errorDescription = 'Je bent niet ingelogd. Log opnieuw in en probeer het opnieuw.';
      } else if (error.message?.includes('NO_DATA')) {
        errorTitle = 'Geen geldige data';
        errorDescription = 'Het bestand bevat geen verwerkbare data. Controleer de kolommen en probeer opnieuw.';
      } else if (error.message?.includes('TOO_MANY_ROWS')) {
        errorTitle = 'Te veel rijen';
        errorDescription = 'Maximum is 400,000 rijen. Probeer een kleiner bestand.';
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: 'destructive'
      });
      
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return {
    uploadFile,
    uploading,
    uploadProgress
  };
}
